import React, { useState } from 'react';
import { Languages, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

const SOURCE = 'it';
const TARGETS = ['en', 'fr', 'de'];
const LANG_LABEL = { it: 'Italiano', en: 'Inglese', fr: 'Francese', de: 'Tedesco' };

// Ensures a value is an i18n object (preserves any existing translations/metadata).
export const toI18n = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : { it: v || '' });

export function TextInput({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-sm font-medium text-paelbi-ink/80">{label}</span>}
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-paelbi-line bg-white px-3 py-2 text-sm text-paelbi-ink outline-none focus:border-paelbi-brown"
      />
    </label>
  );
}

export function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-sm font-medium text-paelbi-ink/80">{label}</span>}
      <textarea
        rows={rows}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-paelbi-line bg-white px-3 py-2 text-sm text-paelbi-ink outline-none focus:border-paelbi-brown"
      />
    </label>
  );
}

const fieldCls = 'w-full rounded-lg border border-paelbi-line bg-white px-3 py-2 text-sm text-paelbi-ink outline-none focus:border-paelbi-brown';

// Single-language (Italian) input. Translations to EN/FR/DE are generated
// automatically on save; a collapsible panel lets the host optionally review
// and manually override each translation (which then stays "locked").
export function I18nInput({ label, value, onChange, multiline = false, langs }) {
  const [open, setOpen] = useState(false);
  const targets = (langs ? langs.filter((l) => l !== SOURCE) : TARGETS);
  const obj = toI18n(value);
  const locked = Array.isArray(obj._locked) ? obj._locked : [];
  const srcText = obj[SOURCE] || '';
  const stale = obj._src !== undefined && obj._src !== srcText && srcText.trim() !== '';

  const setSource = (v) => onChange({ ...obj, [SOURCE]: v });
  const setTarget = (lang, v) => {
    const nextLocked = locked.includes(lang) ? locked : [...locked, lang];
    onChange({ ...obj, [lang]: v, _locked: nextLocked });
  };
  const resetTarget = (lang) => {
    const nextLocked = locked.filter((l) => l !== lang);
    onChange({ ...obj, [lang]: '', _locked: nextLocked });
  };

  return (
    <div data-testid="i18n-field">
      <label className="block">
        <span className="mb-1 flex items-center gap-1.5 text-sm font-medium text-paelbi-ink/80">
          {label}
          <span className="rounded bg-paelbi-bg2 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-paelbi-ink/60">IT</span>
        </span>
        {multiline ? (
          <textarea rows={3} value={srcText} data-testid="i18n-source-input"
            onChange={(e) => setSource(e.target.value)} className={fieldCls} />
        ) : (
          <input value={srcText} data-testid="i18n-source-input"
            onChange={(e) => setSource(e.target.value)} className={fieldCls} />
        )}
      </label>

      <button type="button" data-testid="i18n-translations-toggle" onClick={() => setOpen((v) => !v)}
        className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-paelbi-brown">
        <Languages size={13} />
        Traduzioni automatiche ({targets.map((l) => l.toUpperCase()).join(', ')})
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {stale && (
        <p className="mt-1 text-[11px] text-amber-600">Il testo italiano è cambiato: le traduzioni verranno aggiornate al salvataggio.</p>
      )}

      {open && (
        <div className="mt-2 space-y-3 rounded-lg border border-dashed border-paelbi-line bg-paelbi-bg/50 p-3" data-testid="i18n-translations-panel">
          <p className="text-[11px] text-paelbi-ink/60">
            Scrivi solo in italiano: le altre lingue vengono generate automaticamente. Puoi correggere una traduzione manualmente (resterà bloccata) o ripristinare quella automatica.
          </p>
          {targets.map((lang) => {
            const isLocked = locked.includes(lang);
            const val = obj[lang] || '';
            return (
              <div key={lang}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-paelbi-ink/70">
                    <span className="rounded bg-paelbi-bg2 px-1.5 py-0.5 text-[10px] font-semibold uppercase">{lang}</span>
                    {LANG_LABEL[lang] || lang}
                    {isLocked
                      ? <span className="text-[10px] font-semibold text-paelbi-brown">· modificata manualmente</span>
                      : <span className="text-[10px] text-paelbi-ink/40">· automatica</span>}
                  </span>
                  {isLocked && (
                    <button type="button" data-testid={`i18n-reset-${lang}`} onClick={() => resetTarget(lang)}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-paelbi-ink/50 hover:text-paelbi-brown">
                      <RotateCcw size={11} /> Ripristina automatica
                    </button>
                  )}
                </div>
                {multiline ? (
                  <textarea rows={2} value={val} data-testid={`i18n-target-${lang}`}
                    placeholder="Generata automaticamente al salvataggio"
                    onChange={(e) => setTarget(lang, e.target.value)} className={fieldCls} />
                ) : (
                  <input value={val} data-testid={`i18n-target-${lang}`}
                    placeholder="Generata automaticamente al salvataggio"
                    onChange={(e) => setTarget(lang, e.target.value)} className={fieldCls} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
